import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, fmtDate } from "@/components/Bits";

const Card = ({ label, value, testId }) => (
  <div className="rounded-2xl border border-border bg-card p-5" data-testid={testId}><div className="text-xs font-mono uppercase text-muted-foreground">{label}</div><div className="font-serif text-4xl text-navy mt-1">{value}</div></div>
);
const Breakdown = ({ title, data, labels = {} }) => (
  <div className="rounded-2xl border border-border bg-card p-5"><h3 className="font-serif uppercase text-navy mb-3">{title}</h3>
    {Object.keys(data).length === 0 ? <p className="text-sm text-muted-foreground">No data yet.</p> :
      <ul className="space-y-1 text-sm">{Object.entries(data).map(([k, v]) => <li key={k} className="flex justify-between"><span>{labels[k] || k}</span><span className="font-mono">{v}</span></li>)}</ul>}
  </div>
);

export default function AdminAnalytics() {
  const [a, setA] = useState(null);
  const [oos, setOos] = useState([]);
  useEffect(() => {
    api.get("/admin/analytics").then((r) => setA(r.data));
    api.get("/admin/out-of-scope").then((r) => setOos(r.data));
  }, []);
  if (!a) return null;
  const max = Math.max(1, ...a.per_day.map((d) => d.count));
  return (
    <div>
      <PageHeader eyebrow="Analytics" title="What members are asking, and what's working." />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-6">
        <Card label="Members" value={a.members} testId="an-members" />
        <Card label="Questions (30 days)" value={a.per_day.reduce((s, d) => s + d.count, 0)} testId="an-questions" />
        <Card label="Trial to paid" value={a.trial_to_paid.rate === null ? "–" : `${a.trial_to_paid.rate}%`} testId="an-conversion" />
        <Card label="Out of scope (30 days)" value={a.out_of_scope_30d} testId="an-oos" />
      </div>
      <div className="rounded-2xl border border-border bg-card p-5 mb-6" data-testid="an-per-day">
        <h3 className="font-serif uppercase text-navy mb-4">Questions per day</h3>
        <div className="flex items-end gap-1 h-32">{a.per_day.map((d) => <div key={d.day} title={`${d.day}: ${d.count}`} className="flex-1 rounded-t bg-pinkb/70" style={{ height: `${(d.count * 100) / max}%`, minHeight: d.count ? 4 : 1 }} />)}</div>
      </div>
      <div className="grid gap-4 lg:grid-cols-4 mb-8">
        <div className="lg:col-span-2 min-w-0 rounded-2xl border border-border bg-card p-5" data-testid="an-topics"><h3 className="font-serif uppercase text-navy mb-3">Most common topics</h3>
          {a.topics.length === 0 ? <p className="text-sm text-muted-foreground">No data yet.</p> : <ul className="space-y-1 text-sm">{a.topics.map((t) => <li key={t.title} className="flex justify-between gap-3 min-w-0"><span className="truncate min-w-0">{t.title}</span><span className="font-mono">{t.count}</span></li>)}</ul>}</div>
        <Breakdown title="Tiers" data={a.tiers} labels={{ pro: "Pro", club: "The Roots Club", trial: "Free trial", none: "Lapsed" }} />
        <div className="space-y-4"><Breakdown title="Audience" data={a.audiences} labels={{ cpg: "CPG", hospitality: "Hospitality" }} /><Breakdown title="Growth phase" data={a.phases} /></div>
      </div>
      <div className="rounded-2xl border border-border bg-card p-5" data-testid="out-of-scope-log">
        <h3 className="font-serif uppercase text-navy mb-1">Out-of-scope log</h3>
        <p className="text-xs text-muted-foreground mb-4">Questions the club didn't answer because they weren't about food, drink or hospitality marketing. These are useful for spotting gaps and product ideas.</p>
        {oos.length === 0 ? <p className="text-sm text-muted-foreground">Nothing flagged yet.</p> :
          <ul className="divide-y divide-border text-sm">{oos.map((q) => <li key={q.id} className="py-2"><div>{q.question}</div><div className="text-xs text-muted-foreground">{q.user_name}{q.business_name && ` · ${q.business_name}`} · {fmtDate(q.created_at)}</div></li>)}</ul>}
      </div>
    </div>
  );
}
